import React, { useState } from 'react'

import ActionButton from '@/components/misc/ActionButton'
import IcaewModalHeader from '@/components/modals/IcaewModals/components/IcaewModalHeader'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
} from '@/components/ui/dialog'
import { useUpdateIcaewConsentMutation } from '@/hooks/useUpdateIcaewConsentMutation'
import { CONSENT_DECISION, EXTERNAL_LINKS, MODAL_IDS } from '@/lib/constants'

const IcaewConsentModal: React.FC = () => {
  const [hasClickedOptOut, setHasClickedOptOut] = useState(false)
  const updateConsentMutation = useUpdateIcaewConsentMutation()
  const isProcessing = updateConsentMutation.isPending
  const modalFooterClass = 'flex gap-2'
  const isOptingIn =
    isProcessing && updateConsentMutation.variables === CONSENT_DECISION.OPT_IN
  const isOptingOut =
    isProcessing && updateConsentMutation.variables === CONSENT_DECISION.OPT_OUT

  function renderInitialScreen() {
    return (
      <>
        <DialogBody>
          <p className="mb-4">
            We have partnered with the ICAEW. Are you happy for your data to be
            shared with the ICAEW for monitoring purposes?
          </p>

          <p className="mb-0">
            You can change your mind at any time by going to preferences. For
            further information about how this data will be used, see our{' '}
            <a
              href={EXTERNAL_LINKS.privacyPolicy}
              target="_blank"
              rel="noreferrer"
            >
              privacy policy
            </a>
            .
          </p>
        </DialogBody>

        <DialogFooter className={modalFooterClass}>
          <Button
            variant="outline-destructive"
            onClick={() => setHasClickedOptOut(true)}
          >
            Opt out
          </Button>

          <ActionButton
            variant="success"
            isProcessing={isProcessing}
            onClick={() => {
              updateConsentMutation.mutate(CONSENT_DECISION.OPT_IN)
            }}
          >
            Yes, share data with the ICAEW
          </ActionButton>
        </DialogFooter>
      </>
    )
  }

  function renderOptOutConfirmationScreen() {
    return (
      <>
        <DialogBody>
          <p className="mb-0">
            Are you sure you don't want to share data with the ICAEW?
          </p>
        </DialogBody>

        <DialogFooter className={modalFooterClass}>
          <ActionButton
            disabled={isProcessing}
            isProcessing={isOptingOut}
            variant="outline-destructive"
            onClick={() =>
              updateConsentMutation.mutate(CONSENT_DECISION.OPT_OUT)
            }
          >
            I'm sure. Opt out
          </ActionButton>

          <ActionButton
            disabled={isProcessing}
            isProcessing={isOptingIn}
            variant="success"
            onClick={() => {
              updateConsentMutation.mutate(CONSENT_DECISION.OPT_IN)
            }}
          >
            Opt in
          </ActionButton>
        </DialogFooter>
      </>
    )
  }

  return (
    <Dialog open>
      <DialogContent id={MODAL_IDS.icaewConsentModal} showCloseButton={false}>
        <IcaewModalHeader>Data sharing</IcaewModalHeader>

        {hasClickedOptOut
          ? renderOptOutConfirmationScreen()
          : renderInitialScreen()}
      </DialogContent>
    </Dialog>
  )
}

export default IcaewConsentModal
